/**
 * Admin Profile page — User Profile & Settings.
 *
 * 2026-06-16 SA-console redesign: flat card stack replaced with a header
 * band + 2-col CollapsibleCard layout matching DemoDetail.tsx HoD spec.
 * Left column: Account Identity, Edit Profile Details, Appearance Settings.
 * Right column: Account Security (MfaSection + Change Password sub-block).
 * Layout only — all handlers, state, and data-testids preserved.
 * Raw hex colours removed; token replacements:
 *   old border hex → border-border-default
 *   success green  → text-status-hired-fg
 */
import { useEffect, useState } from "react";
import { IdCard, Palette, ShieldCheck, UserCog } from "lucide-react";
import { useMe } from "@/hooks/queries/auth/useMe";
import {
  useUpdateProfile,
  useChangeMyPassword,
} from "@/hooks/queries/auth/useProfile";
import { MfaSection, PasskeySection } from "@/components/admin/MfaSection";
import { CollapsibleCard } from "@/components/common/CollapsibleCard";
import { Button } from "@/components/common/Button";
import { useTheme } from "@/components/theme/useTheme";
import type { ThemePreference } from "@/lib/theme";

const PASSWORD_HELP = "At least 12 characters. Avoid common passwords.";
const INPUT =
  "mt-1 w-full rounded-md border border-border-default bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary dark:focus:ring-brand-teal";

export default function AdminProfile(): JSX.Element {
  const me = useMe();
  const update = useUpdateProfile();
  const changePw = useChangeMyPassword();
  const { preference, setPreference } = useTheme();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileMsg, setProfileMsg] = useState<string | null>(null);
  const [profileErr, setProfileErr] = useState<string | null>(null);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [pwErr, setPwErr] = useState<string | null>(null);

  // Hydrate the editable inputs when /auth/me arrives.
  useEffect(() => {
    if (me.data) {
      setFirstName(me.data.first_name ?? "");
      setLastName(me.data.last_name ?? "");
    }
  }, [me.data]);

  if (me.isLoading)
    return (
      <div className="text-sm text-text-secondary dark:text-brand-100/70">
        Loading…
      </div>
    );
  if (me.error)
    return (
      <div className="text-sm text-red-600 dark:text-red-400">
        Couldn't load profile: {me.error.message}
      </div>
    );
  if (!me.data) return <div />;

  const submitProfile = (e: React.FormEvent) => {
    e.preventDefault();
    setProfileMsg(null);
    setProfileErr(null);
    const patch: { first_name?: string; last_name?: string } = {};
    if (firstName.trim() && firstName.trim() !== me.data!.first_name)
      patch.first_name = firstName.trim();
    if (lastName.trim() && lastName.trim() !== me.data!.last_name)
      patch.last_name = lastName.trim();
    if (!patch.first_name && !patch.last_name) {
      setProfileMsg("No changes.");
      return;
    }
    update.mutate(patch, {
      onSuccess: () => setProfileMsg("Profile updated."),
      onError: (err) => {
        setProfileErr(err.message || "Could not update profile.");
      },
    });
  };

  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    setPwErr(null);
    if (newPw.length < 12) {
      setPwErr("New password must be at least 12 characters.");
      return;
    }
    if (newPw !== confirmPw) {
      setPwErr("Passwords do not match.");
      return;
    }
    changePw.mutate(
      { current_password: currentPw, new_password: newPw },
      {
        onSuccess: () => {
          setPwMsg("Password changed.");
          setCurrentPw("");
          setNewPw("");
          setConfirmPw("");
        },
        onError: (err) => {
          if (err.error === "invalid_credentials") {
            setPwErr("Current password is incorrect.");
          } else if (err.error === "weak_password") {
            const reasons = Array.isArray(err.details["reasons"])
              ? (err.details["reasons"] as string[]).join(", ")
              : "Password is too weak.";
            setPwErr(reasons);
          } else {
            setPwErr(err.message || "Could not change password.");
          }
        },
      },
    );
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4">
      {/* ── HEADER BAND ─────────────────────────────────────────────────────── */}
      <section className="rounded-xl border border-border-default bg-surface-card px-5 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-subtle text-text-tertiary">
            <UserCog size={18} aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-2xs font-bold uppercase tracking-[0.14em] text-text-tertiary">
              Admin / Profile
            </p>
            <h1 className="truncate text-xl font-bold tracking-tight text-text-primary">
              User Profile &amp; Settings
            </h1>
          </div>
        </div>
      </section>

      {/* ── 2-COL LAYOUT ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {/* LEFT COLUMN */}
        <div className="space-y-3">
          {/* Account Identity */}
          <CollapsibleCard
            title="Account Identity"
            defaultOpen
            leadingIcon={<IdCard size={14} aria-hidden />}
          >
            <dl className="grid grid-cols-1 gap-3 pt-1 text-sm">
              <div>
                <dt className="text-accent-primary dark:text-brand-100/50">Email</dt>
                <dd className="text-text-primary dark:text-text-primary">
                  {me.data.email}
                </dd>
                <p className="mt-1 text-xs text-accent-primary dark:text-brand-100/40">
                  Your email is your sign-in. Contact support to change it.
                </p>
              </div>
              <div>
                <dt className="text-accent-primary dark:text-brand-100/50">Role</dt>
                <dd className="text-text-primary dark:text-text-primary">{me.data.role}</dd>
              </div>
              <div>
                <dt className="text-accent-primary dark:text-brand-100/50">Super Admin</dt>
                <dd className="text-text-primary dark:text-text-primary">
                  {me.data.is_super_admin ? "Yes" : "No"}
                </dd>
              </div>
              {me.data.tenant_slug ? (
                <div>
                  <dt className="text-accent-primary dark:text-brand-100/50">Tenant</dt>
                  <dd className="text-text-primary dark:text-text-primary">
                    {me.data.tenant_slug}
                  </dd>
                </div>
              ) : null}
            </dl>
          </CollapsibleCard>

          {/* Edit Profile Details */}
          <CollapsibleCard
            title="Edit Profile Details"
            defaultOpen
            leadingIcon={<UserCog size={14} aria-hidden />}
          >
            <form onSubmit={submitProfile} className="space-y-3 pt-1">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="block text-text-secondary dark:text-brand-100/70">
                    First name
                  </span>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={INPUT}
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-text-secondary dark:text-brand-100/70">
                    Last name
                  </span>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={INPUT}
                  />
                </label>
              </div>
              {profileErr ? (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {profileErr}
                </p>
              ) : null}
              {profileMsg ? (
                <p className="text-sm text-status-hired-fg">
                  {profileMsg}
                </p>
              ) : null}
              <div className="flex justify-end">
                <Button type="submit" loading={update.isPending}>
                  Save Profile Changes
                </Button>
              </div>
            </form>
          </CollapsibleCard>

          {/* Appearance Settings */}
          <CollapsibleCard
            title="Appearance Settings"
            defaultOpen
            leadingIcon={<Palette size={14} aria-hidden />}
          >
            <div className="pt-1">
              <p className="mb-3 text-sm text-text-secondary dark:text-brand-100/70">
                Choose how CertAIn looks for you. The brand is dark by default.
              </p>
              <div
                role="radiogroup"
                aria-label="Appearance theme"
                className="inline-flex rounded-lg border border-border-default bg-surface-canvas p-1 dark:border-white/10 dark:bg-white/[0.04]"
              >
                {(["light", "dark", "system"] as ThemePreference[]).map((opt) => {
                  const active = preference === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setPreference(opt)}
                      className={`rounded-md px-4 py-1.5 text-sm font-medium capitalize transition-colors ${
                        active
                          ? "bg-white text-text-primary shadow-sm dark:bg-surface-canvas dark:text-text-primary"
                          : "text-text-secondary hover:text-text-primary dark:text-brand-100/60 dark:hover:text-text-primary"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          </CollapsibleCard>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-3">
          {/* Account Security — MFA + Change Password */}
          <CollapsibleCard
            title="Account Security"
            defaultOpen
            leadingIcon={<ShieldCheck size={14} aria-hidden />}
          >
            <div className="pt-1">
              {/*
               * MfaSection renders its own <section> wrapper with card styling.
               * To avoid double-framing, we use a heading-only treatment:
               * suppress MfaSection's outer border visually by rendering it
               * inside a negative-margin wrapper that bleeds past the card padding.
               * MfaSection is NOT modified — we use a wrapper override instead.
               */}
              <p className="mb-2 text-sm font-medium text-text-primary">
                Two-Factor Authentication
              </p>
              <div className="-mx-3.5 -mt-1">
                <MfaSection />
              </div>

              <div className="my-4 border-t border-border-default" />

              {/* WA-1b: Passkeys sub-block — same negative-margin wrapper as MfaSection. */}
              <p className="mb-2 text-sm font-medium text-text-primary">
                Passkeys
              </p>
              <div className="-mx-3.5 -mt-1">
                <PasskeySection />
              </div>

              <div className="my-4 border-t border-border-default" />

              {/* Change Password sub-block */}
              <p className="mb-3 text-sm font-medium text-text-primary">
                Change Password
              </p>
              <form onSubmit={submitPassword} className="space-y-3">
                <label className="block text-sm">
                  <span className="block text-text-secondary dark:text-brand-100/70">
                    Current password
                  </span>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    required
                    className={INPUT}
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-text-secondary dark:text-brand-100/70">
                    New password
                  </span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    required
                    className={INPUT}
                  />
                  <p
                    className="mt-1 text-xs text-accent-primary dark:text-brand-100/50"
                    data-testid="password-help"
                  >
                    {PASSWORD_HELP}
                  </p>
                </label>
                <label className="block text-sm">
                  <span className="block text-text-secondary dark:text-brand-100/70">
                    Confirm new password
                  </span>
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    required
                    className={INPUT}
                  />
                </label>
                {pwErr ? (
                  <p className="text-sm text-red-600 dark:text-red-400">{pwErr}</p>
                ) : null}
                {pwMsg ? (
                  <p className="text-sm text-status-hired-fg">{pwMsg}</p>
                ) : null}
                <div className="flex justify-end">
                  <Button type="submit" loading={changePw.isPending}>
                    Update Password
                  </Button>
                </div>
              </form>
            </div>
          </CollapsibleCard>
        </div>
      </div>
    </div>
  );
}
